import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

// Types
export interface LotCopies {
  id: number;
  code_lot: string;
  prefixe: string;
  matiere?: number | null;
  matiere_code?: string | null;
  matiere_nom?: string | null;
  annee_academique: string;
  semestre: 'S1' | 'S2';
  niveau?: 'L1' | 'L2' | 'L3' | 'M1' | 'M2';
  option?: string;
  pole?: string;
  nombre_copies: number;
  nombre_copies_associees: number;
  taux_association: number;
  type_examen: 'NORMAL' | 'RATTRAPAGE' | 'DEVOIR';
  session?: number | null;
  session_nom?: string | null;
  session_type?: string | null;
  cree_par?: number;
  cree_par_nom?: string;
  date_creation: string;
  codes_generes?: string[];
  est_imprime: boolean;
  date_impression?: string | null;
  imprime_par?: number | null;
  imprime_par_nom?: string | null;
  est_utilise?: boolean;
  date_utilisation?: string | null;
  utilise_par?: number | null;
  utilise_par_nom?: string | null;
  note_site?: string;
  fichier_pdf?: string | null;
}

export interface CopieExamen {
  id: number;
  code_copie: string;
  /** Type d'examen lié à cette copie (NORMAL = session ordinaire, RATTRAPAGE = session de rattrapage) */
  type_examen?: 'NORMAL' | 'RATTRAPAGE' | 'DEVOIR';
  lot: number;
  lot_code: string;
  matiere: number;
  matiere_code: string;
  matiere_nom: string;
  enseignant?: number;
  enseignant_nom?: string;
  etudiant?: number;
  etudiant_matricule?: string;
  etudiant_nom?: string;
  etudiant_classe?: string;
  etudiant_option?: string;
  etudiant_niveau?: string;
  est_associee: boolean;
  est_notee: boolean;
  mode_association?: 'SCAN' | 'MANUEL' | 'IMPORT';
  associee_par?: number;
  associee_par_nom?: string;
  date_association?: string;
}

export interface HistoriqueAssociationCopie {
  id: number;
  copie: number;
  copie_code: string;
  action: 'ASSOCIATION' | 'MODIFICATION' | 'SUPPRESSION';
  ancien_etudiant?: number;
  ancien_etudiant_matricule?: string;
  nouvel_etudiant?: number;
  nouvel_etudiant_matricule?: string;
  effectue_par?: number;
  effectue_par_nom?: string;
  date_action: string;
  commentaire: string;
  adresse_ip?: string;
}

export interface GenererLotPayload {
  nombre_copies: number;
  prefixe_code: string;
  point_depart: number;
  continuite_mode: boolean;
  /**
   * Type de session choisi EXPLICITEMENT par l'agent avant génération
   * (fix 2026-08-27 : ancienne détection basée sur préfixe 'SR' retirée,
   * fragile car cassée dès qu'on change le préfixe). Obligatoire côté API.
   */
  type_examen: 'NORMAL' | 'RATTRAPAGE' | 'DEVOIR';
  session_id?: number;
}

export interface GenererCodesSimplesPayload {
  nombre_copies: number;
  prefixe_code: string;
  continuite_mode: boolean;
}

export interface GenererCodesSimplesResponse {
  codes: string[];
  nombre_copies: number;
  message: string;
  prefixe: string;
  plage: string;
}

export interface AssocierCopiePayload {
  code_copie: string;
  matricule_etudiant: string;
  matiere_id?: number;
  enseignant_id?: number;
  mode_association?: 'SCAN' | 'MANUEL' | 'IMPORT';
  force?: boolean;
  /** Verrou strict : le serveur refuse si copie.type_examen ≠ session_mode */
  session_mode?: 'NORMAL' | 'RATTRAPAGE';
}

export interface AssocierCopiesEnMassePayload {
  associations: Array<{
    code_copie: string;
    matricule_etudiant: string;
  }>;
  mode_association?: 'SCAN' | 'MANUEL' | 'IMPORT';
}

export interface DissocierCopiePayload {
  code_copie: string;
  commentaire?: string;
}

export interface CopieAssocieeItem {
  id: number;
  code_copie: string;
  matiere_id: number;
  matiere_option: string | null;
  etudiant_id: number | null;
  etudiant_matricule: string | null;
  etudiant_nom: string | null;
  etudiant_prenoms: string | null;
  etudiant_classe: string | null;
  enseignant_id: number | null;
  enseignant_nom: string | null;
  est_notee: boolean;
}

export interface CopiesAssocieesResponse {
  matiere_id: number;
  matiere_ids_equivalentes: number[];
  matiere_nom: string;
  niveau: string;
  mode_strict: boolean;
  enseignant_id: number | null;
  total: number;
  copies: CopieAssocieeItem[];
  enseignants_disponibles: Array<{ id: number; nom_complet: string }>;
}

export interface DissocierLotResponse {
  success: boolean;
  total_demandees: number;
  dissociees: number;
  nb_echecs: number;
  echecs: Array<{ code: string; erreur: string }>;
  message: string;
}

export interface StatistiquesLot {
  total_copies: number;
  copies_associees: number;
  copies_non_associees: number;
  copies_notees: number;
  copies_non_notees: number;
  taux_association: number;
  taux_notation: number;
}

export interface StatistiquesGlobales {
  total_lots: number;
  total_copies: number;
  copies_associees: number;
  copies_non_associees: number;
  copies_notees: number;
  etudiants_ayant_compose: number;
}

 export interface ProchainCodeResponse {
  prefixe_code: string;
  dernier_numero: number;
  prochain_code: string;
 }

export interface ModeleCopie {
  id: number;
  nom: string;
  description: string;
  image_template: string;
  image_verso: string | null;
  recto_verso: boolean;
  barcode_x: number;
  barcode_y: number;
  barcode_largeur: number;
  barcode_hauteur: number;
  barcode_couleur: string;
  code_texte_couleur: string;
  afficher_code_texte: boolean;
  code_texte_x: number | null;
  code_texte_y: number | null;
  code_texte_taille: number;
  code_texte_police: string;
  format_page: 'A4' | 'A3';
  orientation: 'portrait' | 'paysage';
  cree_par: number | null;
  cree_par_nom: string;
  date_creation: string;
  date_modification: string;
  est_actif: boolean;
}

export interface StatsImpression {
  total_copies: number;
  imprimees: number;
  non_imprimees: number;
  taux_impression: number;
  par_utilisateur: Array<{ utilisateur: string; nombre: number }>;
}

export const copiesApi = createApi({
  reducerPath: 'copiesApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['LotCopies', 'CopieExamen', 'HistoriqueAssociation', 'Statistiques', 'ModeleCopie'],
  endpoints: (builder) => ({
    // LOTS DE COPIES
    getLotsCopies: builder.query<LotCopies[], Record<string, any> | void>({
      query: (params) => ({
        url: '/lots-copies/',
        params: params ?? undefined,
      }),
      transformResponse: (response: any) => {
        // Gérer la réponse paginée ou directe
        if (response && response.results) {
          return response.results;
        }
        return Array.isArray(response) ? response : [];
      },
      providesTags: ['LotCopies'],
    }),

    getProchainCode: builder.query<ProchainCodeResponse, { prefixe_code?: string } | void>({
      query: (params) => ({
        url: '/lots-copies/prochain_code/',
        params: params ?? undefined,
      }),
      providesTags: ['Statistiques'],
    }),

    getLotCopiesById: builder.query<LotCopies, number>({
      query: (id) => `/lots-copies/${id}/`,
      providesTags: ['LotCopies'],
    }),

    genererLotCopies: builder.mutation<any, GenererLotPayload>({
      query: (data) => ({
        url: '/lots-copies/generer/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['LotCopies', 'Statistiques'],
    }),

    exportLotExcel: builder.query<Blob, number>({
      query: (lotId) => ({
        url: `/lots-copies/${lotId}/export_excel/`,
        responseHandler: (response) => response.blob(),
      }),
    }),

    getStatistiquesLot: builder.query<StatistiquesLot, number>({
      query: (lotId) => `/lots-copies/${lotId}/statistiques/`,
      providesTags: ['Statistiques'],
    }),

    getStatistiquesGlobales: builder.query<StatistiquesGlobales, Record<string, any> | void>({
      query: (params) => ({
        url: '/lots-copies/statistiques_globales/',
        params: params ?? undefined,
      }),
      providesTags: ['Statistiques'],
    }),

    marquerLotImprime: builder.mutation<LotCopies, { lotId: number; note_site?: string }>({
      query: ({ lotId, note_site }) => ({
        url: `/lots-copies/${lotId}/marquer_imprime/`,
        method: 'POST',
        body: note_site ? { note_site } : {},
      }),
      invalidatesTags: ['LotCopies'],
    }),

    marquerLotNonImprime: builder.mutation<LotCopies, number>({
      query: (lotId) => ({
        url: `/lots-copies/${lotId}/marquer_non_imprime/`,
        method: 'POST',
      }),
      invalidatesTags: ['LotCopies'],
    }),

    marquerLotUtilise: builder.mutation<LotCopies, { lotId: number; note_site?: string }>({
      query: ({ lotId, note_site }) => ({
        url: `/lots-copies/${lotId}/marquer_utilise/`,
        method: 'POST',
        body: note_site ? { note_site } : {},
      }),
      invalidatesTags: ['LotCopies'],
    }),

    marquerLotNonUtilise: builder.mutation<LotCopies, number>({
      query: (lotId) => ({
        url: `/lots-copies/${lotId}/marquer_non_utilise/`,
        method: 'POST',
      }),
      invalidatesTags: ['LotCopies'],
    }),

    deleteLotCopies: builder.mutation<any, { lotId: number; force?: boolean }>({
      query: ({ lotId, force }) => ({
        url: `/lots-copies/${lotId}/`,
        method: 'DELETE',
        params: force ? { force: 'true' } : undefined,
      }),
      invalidatesTags: ['LotCopies', 'CopieExamen', 'Statistiques'],
    }),

    // COPIES D'EXAMENS
    getCopiesExamens: builder.query<CopieExamen[], Record<string, any> | void>({
      query: (params) => ({
        url: '/copies-examens/',
        params: params ?? undefined,
      }),
      transformResponse: (response: any) => {
        // Gérer la réponse paginée ou directe
        if (response && response.results) {
          return response.results;
        }
        return Array.isArray(response) ? response : [];
      },
      providesTags: ['CopieExamen'],
    }),

    getCopieExamenById: builder.query<CopieExamen, number>({
      query: (id) => `/copies-examens/${id}/`,
      providesTags: ['CopieExamen'],
    }),

    rechercherCopieParCode: builder.query<CopieExamen, string>({
      query: (code) => `/copies-examens/rechercher_par_code/?code=${code}`,
      providesTags: ['CopieExamen'],
    }),

    associerCopie: builder.mutation<any, AssocierCopiePayload>({
      query: (data) => ({
        url: '/copies-examens/associer/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['CopieExamen', 'LotCopies', 'Statistiques', 'HistoriqueAssociation'],
    }),

    associerCopiesEnMasse: builder.mutation<any, AssocierCopiesEnMassePayload>({
      query: (data) => ({
        url: '/copies-examens/associer_en_masse/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['CopieExamen', 'LotCopies', 'Statistiques', 'HistoriqueAssociation'],
    }),

    dissocierCopie: builder.mutation<any, DissocierCopiePayload>({
      query: (data) => ({
        url: '/copies-examens/dissocier/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['CopieExamen', 'LotCopies', 'Statistiques', 'HistoriqueAssociation'],
    }),

    /** Liste les copies associées d'une matière (optionnellement filtrées par formateur). */
    getCopiesAssocieesParMatiere: builder.query<CopiesAssocieesResponse,
      { matiere_id: number; enseignant_id?: number }>({
      query: ({ matiere_id, enseignant_id }) => {
        const qs = new URLSearchParams({ matiere_id: String(matiere_id) });
        if (enseignant_id) qs.set('enseignant_id', String(enseignant_id));
        return `/copies-examens/associees-par-matiere/?${qs.toString()}`;
      },
      providesTags: ['CopieExamen'],
    }),

    /** Dissocie en lot plusieurs copies (par codes ou ids). */
    dissocierLotCopies: builder.mutation<DissocierLotResponse,
      { codes?: string[]; ids?: number[]; commentaire?: string }>({
      query: (body) => ({
        url: '/copies-examens/dissocier-lot/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CopieExamen', 'LotCopies', 'Statistiques', 'HistoriqueAssociation'],
    }),

    // MODÈLES DE COPIES
    getModelesCopies: builder.query<ModeleCopie[], void>({
      query: () => '/modeles-copies/',
      transformResponse: (response: any) =>
        response?.results ? response.results : Array.isArray(response) ? response : [],
      providesTags: ['ModeleCopie'],
    }),

    getModeleCopieById: builder.query<ModeleCopie, number>({
      query: (id) => `/modeles-copies/${id}/`,
      providesTags: ['ModeleCopie'],
    }),

    createModeleCopie: builder.mutation<ModeleCopie, FormData>({
      query: (formData) => ({
        url: '/modeles-copies/',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['ModeleCopie'],
    }),

    updateModeleCopie: builder.mutation<ModeleCopie, { id: number; data: FormData | Record<string, any> }>({
      query: ({ id, data }) => ({
        url: `/modeles-copies/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['ModeleCopie'],
    }),

    deleteModeleCopie: builder.mutation<void, number>({
      query: (id) => ({
        url: `/modeles-copies/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ModeleCopie'],
    }),

    genererCopiesPDF: builder.mutation<Blob, { modeleId: number; codes: string[] }>({
      query: ({ modeleId, codes }) => ({
        url: `/modeles-copies/${modeleId}/generer_pdf/`,
        method: 'POST',
        body: { codes },
        responseHandler: async (response: Response) => {
          if (!response.ok) {
            const text = await response.text();
            try { return JSON.parse(text); } catch { return { error: text }; }
          }
          return response.blob();
        },
      }),
      invalidatesTags: ['LotCopies'],
    }),

    genererPdfLots: builder.mutation<Blob, { modeleId: number; lotIds: number[] }>({
      query: ({ modeleId, lotIds }) => ({
        url: `/modeles-copies/${modeleId}/generer_pdf_lots/`,
        method: 'POST',
        body: { lot_ids: lotIds },
        responseHandler: async (response: Response) => {
          if (!response.ok) {
            const text = await response.text();
            try { return JSON.parse(text); } catch { return { error: text }; }
          }
          const blob = await response.blob();
          // Attacher le content-type pour que le frontend sache si c'est PDF ou ZIP
          (blob as any)._contentType = response.headers.get('content-type') || '';
          return blob;
        },
      }),
      invalidatesTags: ['LotCopies'],
    }),

    getStatsImpression: builder.query<StatsImpression, void>({
      query: () => '/modeles-copies/stats_impression/',
      providesTags: ['ModeleCopie'],
    }),

    // STATS COPIES (counts rapides sans charger toutes les copies)
    getStatsCopies: builder.query<{
      total: number; associees: number; non_associees: number; notees: number; orphelines: number;
    }, void>({
      query: () => '/copies-examens/stats/',
      providesTags: ['CopieExamen'],
    }),

    // PROGRESSION EDT — matières planifiées (via EDT) vs copies déjà associées
    // Permet de suivre au quotidien l'écart attendu ↔ associé pour chaque matière.
    getProgressionEDT: builder.query<{
      session: {
        id: number; nom: string; annee_academique: string; semestre: number;
        type_session: string; type_formation: string;
        date_debut: string | null; date_fin: string | null;
      };
      resume: {
        nb_creneaux: number; total_attendu: number; total_associe: number;
        total_ecart: number; taux_couverture_pct: number;
        type_examen: 'NORMAL' | 'RATTRAPAGE'; genere_le: string;
      };
      par_jour: Array<{
        date: string; jour_semaine: string; nb_creneaux: number;
        attendu: number; associe: number; ecart: number; taux_pct: number;
      }>;
      par_matiere: Array<{
        matiere: string; niveau: string; groupe: string;
        attendu: number; associe: number; ecart: number; dates: string[];
      }>;
      par_niveau: Array<{
        niveau: string; attendu: number; associe: number; ecart: number;
        nb_creneaux: number; taux_pct: number;
      }>;
      par_pole: Array<{
        pole: string; attendu: number; associe: number; ecart: number;
        nb_creneaux: number; taux_pct: number;
      }>;
      par_site: Array<{
        site: string; attendu: number; associe: number; ecart: number;
        nb_etudiants: number; taux_pct: number;
      }>;
      details: Array<{
        creneau_id: number; date: string; jour_semaine: string; heure: string;
        matiere: string; matiere_ids: number[]; niveau: string; groupe: string;
        options: Array<{ option: string; effectif: number }>;
        effectif_attendu: number; copies_associees: number; ecart: number; taux_pct: number;
      }>;
    }, { session_examen_id: number; type_examen?: 'NORMAL' | 'RATTRAPAGE' }>({
      query: ({ session_examen_id, type_examen }) => {
        const params: any = { session_examen_id };
        if (type_examen) params.type_examen = type_examen;
        return { url: '/copies-examens/progression-edt/', params };
      },
      providesTags: ['CopieExamen'],
    }),

    // Diagnostic : copies associées à une matière absente des créneaux EDT
    // → détecte les erreurs d'association (agent qui choisit la mauvaise matière)
    getCopiesHorsEDT: builder.query<{
      session: { id: number; nom: string; semestre: number; type_session: string; date_debut: string | null; date_fin: string | null };
      type_examen: 'NORMAL' | 'RATTRAPAGE';
      nb_matieres_hors_edt: number;
      nb_copies_hors_edt: number;
      matieres_hors_edt: Array<{
        matiere_id: number; matiere_code: string; matiere_nom: string;
        semestre: number; niveau: string; nb_copies: number;
        exemples_codes: string[];
        suggestions: Array<{ id: number; code_matiere: string; nom_matiere: string; semestre: number; niveau: string; mots_communs: string[]; score: number }>;
      }>;
    }, { session_examen_id: number; type_examen?: 'NORMAL' | 'RATTRAPAGE' }>({
      query: ({ session_examen_id, type_examen }) => {
        const params: any = { session_examen_id };
        if (type_examen) params.type_examen = type_examen;
        return { url: '/copies-examens/copies-hors-edt/', params };
      },
      providesTags: ['CopieExamen'],
    }),

    // ORPHELINES
    compterOrphelines: builder.query<{
      total_copies: number; total_lots: number; orphelines: number;
      orphelines_associees: number; orphelines_non_associees: number; copies_valides: number;
    }, void>({
      query: () => '/copies-examens/compter_orphelines/',
      providesTags: ['CopieExamen'],
    }),

    purgerOrphelines: builder.mutation<
      { success: boolean; message: string; supprimees: number },
      { forcer?: boolean }
    >({
      query: (body) => ({ url: '/copies-examens/purger_orphelines/', method: 'POST', body }),
      invalidatesTags: ['CopieExamen', 'Statistiques'],
    }),

    // HISTORIQUE
    getHistoriqueAssociations: builder.query<
      { results: HistoriqueAssociationCopie[]; count: number; next: string | null; previous: string | null },
      { page?: number; page_size?: number; search?: string; action?: string; date_debut?: string; date_fin?: string; matiere?: string; utilisateur?: string; classe?: string; formateur?: string } | void
    >({
      query: (params) => ({
        url: '/historique-associations-copies/',
        params: params ?? undefined,
      }),
      transformResponse: (response: any) => {
        if (response && response.results) {
          return { results: response.results, count: response.count || 0, next: response.next, previous: response.previous };
        }
        const arr = Array.isArray(response) ? response : [];
        return { results: arr, count: arr.length, next: null, previous: null };
      },
      providesTags: ['HistoriqueAssociation'],
    }),

    getHistoriqueStats: builder.query<{
      stats_utilisateurs: Array<{
        nom: string;
        total: number;
        moyenne_par_jour: number;
        jours: Array<{ date: string; count: number }>;
        matieres: Array<{ matiere: string; count: number }>;
      }>;
      matieres_uniques: string[];
      utilisateurs_uniques: string[];
      classes_uniques: string[];
      formateurs_uniques: string[];
      total: number;
    }, void>({
      query: () => '/historique-associations-copies/stats/',
      providesTags: ['HistoriqueAssociation'],
    }),

    attribuerEnseignant: builder.mutation<
      { success: boolean; nb_mises_a_jour: number; message: string },
      { enseignant_id: number; matiere_id?: number; copie_ids?: number[]; classes?: string[] }
    >({
      query: (data) => ({
        url: '/copies-examens/attribuer_enseignant/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['CopieExamen', 'HistoriqueAssociation'],
    }),

    transfererCopies: builder.mutation<
      // Deux formats de réponse selon dry_run
      any,
      {
        source_matiere_id: number;
        dest_matiere_id?: number;
        classes?: string[];
        enseignant_source_id?: number;
        enseignant_cible_id?: number;
        type_examen?: 'NORMAL' | 'RATTRAPAGE';
        code_copies?: string[];
        dry_run?: boolean;
        force_niveau_different?: boolean;
        // Rétrocompat
        enseignant_id?: number;
      }
    >({
      query: (data) => ({
        url: '/copies-examens/transferer_copies/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['CopieExamen', 'HistoriqueAssociation'],
    }),

    retirerEnseignant: builder.mutation<
      { success: boolean; nb_mises_a_jour: number; message: string },
      { matiere_id: number; enseignant_id: number; classes?: string[] }
    >({
      query: (data) => ({
        url: '/copies-examens/retirer_enseignant/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['CopieExamen', 'HistoriqueAssociation'],
    }),

    getStatsMatiereEnseignant: builder.query<{
      total_copies: number;
      total_sans_enseignant: number;
      matieres: Array<{
        matiere_id: number;
        matiere_nom: string;
        matiere_code: string;
        niveau: string;
        total: number;
        avec_enseignant: number;
        sans_enseignant: number;
        enseignants: Array<{ id: number; nom: string; count: number; classes: Array<{ classe: string; count: number }> }>;
        classes: Array<{ classe: string; count: number; anomalie: boolean; raison: string; etudiants: Array<{ code_copie: string; nom: string; matricule: string }> }>;
      }>;
    }, void>({
      query: () => '/copies-examens/stats_par_matiere_enseignant/',
      providesTags: ['CopieExamen'],
    }),

    getCopiesSansFormateur: builder.query<{
      total_copies_sans_formateur: number;
      total_matieres: number;
      total_avec_suggestion: number;
      total_haute_confiance: number;
      niveaux: string[];
      poles: string[];
      classes: string[];
      matieres: Array<{
        matiere_id: number;
        matiere_nom: string;
        matiere_code: string;
        niveau: string;
        semestre: number;
        option: string;
        nb_copies: number;
        classes: Array<{ classe: string; count: number }>;
        etudiants: Array<{
          copie_id: number;
          code_copie: string;
          matricule: string;
          nom: string;
          prenoms: string;
          classe: string;
          option: string;
          pole: string;
        }>;
        associes_par: Array<{ username: string; count: number }>;
        suggestions: Array<{
          id: number;
          nom: string;
          source: string;
          confiance: string;
          detail?: string;
          classes_communes?: string[];
        }>;
      }>;
    }, { niveau?: string; pole?: string; classe?: string } | void>({
      query: (params) => {
        const p = new URLSearchParams();
        if (params?.niveau) p.set('niveau', params.niveau);
        if (params?.pole) p.set('pole', params.pole);
        if (params?.classe) p.set('classe', params.classe);
        return `/copies-examens/copies_sans_formateur/?${p.toString()}`;
      },
      providesTags: ['CopieExamen'],
    }),

    getRecapMatieresAssociees: builder.query<{
      total_copies: number;
      total_matieres: number;
      niveaux: string[];
      poles: string[];
      options: string[];
      matieres: Array<{
        matiere_id: number;
        matiere_code: string;
        matiere_nom: string;
        niveau: string;
        semestre: number;
        option: string;
        total_copies: number;
        classes: Array<{ classe: string; count: number }>;
        classes_label: string;
        enseignants: Array<{ id: number; nom: string }>;
        enseignant_label: string;
        utilisateurs: Array<{ nom: string; count: number }>;
        utilisateur_label: string;
        poles: Record<string, number>;
      }>;
    }, { niveau?: string; pole?: string; option?: string }>({
      query: (params) => {
        const p = new URLSearchParams();
        if (params?.niveau) p.set('niveau', params.niveau);
        if (params?.pole) p.set('pole', params.pole);
        if (params?.option) p.set('option', params.option);
        return `/copies-examens/recap_matieres_associees/?${p.toString()}`;
      },
      providesTags: ['CopieExamen'],
    }),
  }),
});

export const {
  useGetLotsCopiesQuery,
  useGetProchainCodeQuery,
  useGetLotCopiesByIdQuery,
  useGenererLotCopiesMutation,
  useLazyExportLotExcelQuery,
  useGetStatistiquesLotQuery,
  useGetStatistiquesGlobalesQuery,
  useMarquerLotImprimeMutation,
  useMarquerLotNonImprimeMutation,
  useMarquerLotUtiliseMutation,
  useMarquerLotNonUtiliseMutation,
  useDeleteLotCopiesMutation,
  useGetCopiesExamensQuery,
  useGetCopieExamenByIdQuery,
  useLazyRechercherCopieParCodeQuery,
  useAssocierCopieMutation,
  useAssocierCopiesEnMasseMutation,
  useDissocierCopieMutation,
  useGetCopiesAssocieesParMatiereQuery,
  useLazyGetCopiesAssocieesParMatiereQuery,
  useDissocierLotCopiesMutation,
  useGetStatsCopiesQuery,
  useGetProgressionEDTQuery,
  useGetCopiesHorsEDTQuery,
  useLazyCompterOrphelinesQuery,
  usePurgerOrphelinesMutation,
  useGetHistoriqueAssociationsQuery,
  useAttribuerEnseignantMutation,
  useGetStatsMatiereEnseignantQuery,
  // Modèles de copies
  useGetModelesCopiesQuery,
  useGetModeleCopieByIdQuery,
  useCreateModeleCopieMutation,
  useUpdateModeleCopieMutation,
  useDeleteModeleCopieMutation,
  useGenererCopiesPDFMutation,
  useGenererPdfLotsMutation,
  useGetStatsImpressionQuery,
  useGetRecapMatieresAssocieesQuery,
  useRetirerEnseignantMutation,
  useTransfererCopiesMutation,
  useGetCopiesSansFormateurQuery,
  useGetHistoriqueStatsQuery,
} = copiesApi;
